import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { DataTable, download } from "@/lib/admin";
import { MultiCheck } from "@/components/admin/FilterBar";
import { Notice } from "@/components/form/FormFeedback";
import { Loading } from "@/components/layout/RequireAuth";

const EVENT_LABELS = {
    login: "Accesso", login_fallito: "Accesso fallito", logout: "Uscita", pagina_vista: "Pagina vista", registrazione: "Registrazione",
    email_confermata: "Email confermata", assessment_iniziato: "Assessment iniziato", assessment_completato: "Assessment completato",
    chat_ai_aperta: "Chat AI aperta", chat_ai_bloccata_consenso: "Chat AI bloccata (consenso)", consenso_ai_dato: "Consenso AI dato",
    consenso_ai_revocato: "Consenso AI revocato", export: "Export",
};
const dt = (iso) => new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });

const COLUMNS = [
    { key: "ts", label: "Data e ora", render: (r) => <span className="whitespace-nowrap">{dt(r.ts)}</span> },
    { key: "email", label: "Utente", render: (r) => r.email || "—" },
    { key: "azienda", label: "Azienda", render: (r) => r.azienda || "—" },
    { key: "ruolo", label: "Ruolo" },
    { key: "sede", label: "Sede", render: (r) => r.sede || "—" },
    { key: "evento", label: "Evento", render: (r) => <>{EVENT_LABELS[r.evento] || r.evento}{r.dettaglio && <span className="block text-edi-muted">{r.dettaglio}</span>}</> },
    { key: "pagina", label: "Pagina", render: (r) => (r.pagina ? <span className="break-all">{r.pagina}</span> : "—") },
    { key: "dispositivo", label: "Dispositivo" },
    { key: "browser", label: "Browser" },
];

export const AccessLog = () => {
    const { search } = useLocation();
    const [params, setParams] = useSearchParams();
    const [d, setD] = useState(null);
    const [q, setQ] = useState(params.get("q") || "");
    const [msg, setMsg] = useState(null);
    const page = Number(params.get("page") || 1);
    const qs = () => { const n = new URLSearchParams(search); n.delete("vista"); if (n.get("periodo") === "custom" && !n.get("from")) n.delete("periodo"); return n; };
    useEffect(() => {
        api.get(`/admin/analytics/log?${qs()}`).then(({ data }) => setD(data)).catch((e) => setMsg({ kind: "error", text: errorMessage(e) }));
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps
    const update = (mutate) => { const n = new URLSearchParams(params); mutate(n); setParams(n); };
    const exportCsv = async () => {
        setMsg(null);
        try { const n = qs(); n.delete("page"); const name = await download(`/admin/analytics/log/export?${n}`); setMsg({ kind: "success", text: `File ${name} scaricato` }); }
        catch (e) { setMsg({ kind: "error", text: errorMessage(e) }); }
    };
    if (!d) return msg ? <Notice kind="error" role="alert">{msg.text}</Notice> : <Loading />;
    const pages = Math.max(1, Math.ceil(d.total / d.page_size));
    return (
        <div className="space-y-4" data-testid="access-log">
            <div className="edi-card p-4 grid gap-3 sm:grid-cols-2 items-end">
                <MultiCheck name="tipo" options={d.event_types.map((t) => ({ value: t, label: EVENT_LABELS[t] }))} selected={params.getAll("tipo")}
                    onChange={(v) => update((n) => { n.delete("tipo"); v.forEach((x) => n.append("tipo", x)); n.delete("page"); })} />
                <form role="search" className="flex flex-wrap gap-2 items-end" onSubmit={(e) => { e.preventDefault(); update((n) => { if (q.trim()) n.set("q", q.trim()); else n.delete("q"); n.delete("page"); }); }}>
                    <div className="flex-1 min-w-[12rem]"><label htmlFor="log-search" className="edi-help block">Cerca per email o ragione sociale</label>
                        <input id="log-search" type="search" className="edi-input" value={q} onChange={(e) => setQ(e.target.value)} data-testid="log-search-input" /></div>
                    <button type="submit" className="btn-secondary py-2" data-testid="log-search-button">Cerca</button>
                </form>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p role="status" data-testid="log-total"><strong>{d.total.toLocaleString("it-IT")}</strong> eventi</p>
                <button type="button" className="btn-primary py-2" onClick={exportCsv} data-testid="log-export-csv"><Download aria-hidden="true" size={16} /> Esporta CSV</button>
            </div>
            <div aria-live="polite">{msg && <Notice kind={msg.kind} role={msg.kind === "error" ? "alert" : "status"} testId="log-message">{msg.text}</Notice>}</div>
            <DataTable caption="Registro accessi (dal più recente)" testId="log-table" rows={d.items} columns={COLUMNS} empty="Nessun dato disponibile per il periodo selezionato" />
            <nav aria-label="Paginazione registro" className="flex flex-wrap items-center gap-3">
                <button type="button" className="btn-secondary py-2" disabled={page <= 1} onClick={() => update((n) => n.set("page", page - 1))} data-testid="log-prev">Precedente</button>
                <span data-testid="log-page">Pagina {page} di {pages}</span>
                <button type="button" className="btn-secondary py-2" disabled={page >= pages} onClick={() => update((n) => n.set("page", page + 1))} data-testid="log-next">Successiva</button>
            </nav>
        </div>
    );
};
